import React, { useEffect, useState, useContext, useRef } from "react";
import iconSprite from "bootstrap-italia/dist/svg/sprite.svg";
import Pagination from "../../Components/Pagination";
import Repo from "../../Utility/Repo";
import { Link } from "react-router-dom";
import ModalComponent from "../../Components/ModalComponent";
import { Input } from "design-react-kit";
import Loader from "../../Components/Loader";
import Select from "../../Components/Select";
import { TokenContext } from "../../Components/TokenContext";
import { useLocation, useHistory } from "react-router-dom";
import EmailIcon from "../../Components/EmailIcon ";
import PhoneIcon from "../../Components/PhoneIcon";

const getLastIsee = (user) =>
  Array.isArray(user?.isees) && user.isees.length > 0
    ? user.isees[user.isees.length - 1]
    : null;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ListUsers(props) {
  const apiToken = useContext(TokenContext);
  const [usersListTemp, setUsersListTemp] = useState();
  const [usersList, setUsersList] = useState([]);
  const [isOpen, toggleModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countUser, setCountUser] = useState(0);
  const [removableUser, setRemovableUser] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const query = useQuery();
  const history = useHistory();

  const getStateFromQuery = () => {
    if (query.get("loaded") === "true") {
      return "LOADED";
    }
    return query.get("state") || "";
  };

  const initialFilter = {
    firstName: query.get("firstName") || "",
    lastName: query.get("lastName") || "",
    fiscalCode: query.get("fiscalCode") || "",
    email: query.get("email") || "",
    phoneNumber: query.get("phoneNumber") || "",
    state: getStateFromQuery(),
  };

  const [filter, setFilter] = useState(initialFilter);
  const fetchRequestId = useRef(0);

  const toApiFilter = (currentFilter) => ({
    firstName: currentFilter.firstName || "",
    lastName: currentFilter.lastName || "",
    fiscalCode: currentFilter.fiscalCode || "",
    email: currentFilter.email || "",
    phoneNumber: currentFilter.phoneNumber || "",
    loaded: currentFilter.state === "LOADED",
    state: currentFilter.state || null,
  });

  const toSearch = (currentFilter) => {
    const params = new URLSearchParams();
    params.set("firstName", currentFilter.firstName || "");
    params.set("lastName", currentFilter.lastName || "");
    params.set("fiscalCode", currentFilter.fiscalCode || "");
    params.set("email", currentFilter.email || "");
    params.set("phoneNumber", currentFilter.phoneNumber || "");
    params.set("loaded", String(currentFilter.state === "LOADED"));
    params.set("state", currentFilter.state || "");
    return params.toString();
  };

  const fetchUsers = async (currentFilter) => {
    const requestId = ++fetchRequestId.current;
    setLoading(true);

    try {
      const response = await Repo.getUsersList(
        apiToken,
        toApiFilter(currentFilter),
      );

      if (requestId !== fetchRequestId.current) return;

      if (response?.data && Array.isArray(response.data)) {
        setUsersListTemp(response.data);
        setCountUser(response.data.length);
      } else {
        setUsersListTemp([]);
        setUsersList([]);
        setCountUser(0);
      }
    } catch (error) {
      console.error(error);
      if (requestId !== fetchRequestId.current) return;
      setUsersListTemp([]);
      setUsersList([]);
      setCountUser(0);
    } finally {
      if (requestId === fetchRequestId.current) {
        setLoading(false);
      }
    }
  };

  const sendFilter = () => {
    setCurrentPage(1);
    history.replace({ search: toSearch(filter) });
    fetchUsers(filter);
  };

  const [sortConfig, setSortConfig] = useState({
    key: "lastName",
    direction: "asc",
  });

  const sortedUsers = React.useMemo(() => {
    if (!usersListTemp) return [];

    const sortable = [...usersListTemp];

    if (!sortConfig.key) return sortable;

    sortable.sort((a, b) => {
      let aValue;
      let bValue;

      // === Cognome + Nome combinato
      if (sortConfig.key === "lastName") {
        aValue = `${a.lastName ?? ""} ${a.firstName ?? ""}`;
        bValue = `${b.lastName ?? ""} ${b.firstName ?? ""}`;
      }

      // === ISEE ultimo
      else if (sortConfig.key === "isee") {
        aValue =
          a.isees?.length > 0
            ? parseFloat(a.isees[a.isees.length - 1].value) || 0
            : 0;

        bValue =
          b.isees?.length > 0
            ? parseFloat(b.isees[b.isees.length - 1].value) || 0
            : 0;
      }

      // === Date
      else if (
        sortConfig.key === "birthDate" ||
        sortConfig.key === "dataDecesso" ||
        sortConfig.key === "dataAggAnpr"
      ) {
        const parseItalianDate = (value) => {
          if (!value) return 0;

          const parts = value.split("/");
          if (parts.length !== 3) return 0;

          const [day, month, year] = parts;
          return new Date(`${year}-${month}-${day}`).getTime();
        };

        aValue = parseItalianDate(a[sortConfig.key]);
        bValue = parseItalianDate(b[sortConfig.key]);
      }

      // === Default string / number
      else {
        aValue = a[sortConfig.key] ?? "";
        bValue = b[sortConfig.key] ?? "";
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;

      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

      return 0;
    });

    return sortable;
  }, [usersListTemp, sortConfig]);

  const getAnprStatus = (dataAggAnpr, dataDecesso) => {
    // Se deceduto → ROSSO
    if (dataDecesso && dataDecesso.trim().length > 0) {
      return "danger";
    }

    // Se mai aggiornato → ROSSO
    if (!dataAggAnpr) {
      return "danger";
    }

    const parseItalianDate = (value) => {
      const parts = value.split("/");
      if (parts.length !== 3) return null;

      const [day, month, year] = parts;
      return new Date(`${year}-${month}-${day}`);
    };

    const data = parseItalianDate(dataAggAnpr);
    if (!data) return "danger";

    const oggi = new Date();
    const diffMs = oggi - data;
    const diffGiorni = diffMs / (1000 * 60 * 60 * 24);

    if (diffGiorni <= 30) {
      return "success"; // verde
    }

    return "warning"; // giallo
  };

  const requestSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;

    return (
      <svg className="icon ms-1">
        <use
          href={`${iconSprite}#${
            sortConfig.direction === "asc"
              ? "it-arrow-up-triangle"
              : "it-arrow-down-triangle"
          }`}
        />
      </svg>
    );
  };

  const clearFilter = () => {
    const resetFilter = {
      firstName: "",
      lastName: "",
      fiscalCode: "",
      email: "",
      phoneNumber: "",
      state: "",
    };

    setFilter(resetFilter);
    history.replace({ search: "" });
    fetchUsers(resetFilter);
  };

  const removableUserName = [removableUser.firstName, removableUser.lastName]
    .filter(Boolean)
    .join(" ");

  const closeRemoveUserModal = () => {
    toggleModal(false);
    setRemovableUser({});
  };

  const removeUserAPI = () => {
    if (!removableUser.id) {
      closeRemoveUserModal();
      return;
    }

    setLoading(true);

    Repo.postDeleteUser(apiToken, removableUser.id)
      .then((response) => {
        if (response.status == 204) {
          closeRemoveUserModal();
          fetchUsers(filter);
          return;
        }

        setLoading(false);
      })
      .catch((error) => {
        closeRemoveUserModal();
        setLoading(false);
        console.log(error);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Previene il comportamento predefinito
      sendFilter(); // Esegue la ricerca
    }
  };

  useEffect(() => {
    if (!apiToken) return;
    fetchUsers(filter);
  }, [apiToken]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  return (
    <>
      <div>
        <h1>Lista utenti</h1>
        <div className="row">
          <div className="clearfix col-lg-8 py-lg-2">
            <p>
              Tutti gli utenti caricati sul sistema. Da qui è possibile cercare
              un utente o aggiungere un nuovo utente.{" "}
            </p>
          </div>

          <div className="col-lg-4 clearfix">
            <button
              type="button"
              className="btn btn-outline-success float-right p-2"
              onClick={() =>
                (window.location.href =
                  window.location.pathname + "/nuovo_utente")
              }
            >
              <svg className="icon icon-success">
                <use href={`${iconSprite}#it-plus-circle`} />
              </svg>{" "}
              <span className="align-middle">Nuovo Utente</span>
            </button>
          </div>
        </div>

        {/*Inizo cerca*/}

        {/*Inizo cerca*/}
        <div className="row" onKeyDown={handleKeyDown}>
          <div className="form-group col-md-4">
            <Input
              id="first_name"
              required
              label="Nome"
              type="text"
              name="first_name"
              value={filter.firstName} // Aggiunto il valore
              autoComplete="off"
              onChange={(e) =>
                setFilter({ ...filter, firstName: e.target.value })
              }
            />
          </div>
          <div className="form-group col-md-4">
            <Input
              id="last_name"
              required
              label="Cognome"
              type="text"
              name="last_name"
              value={filter.lastName} // Aggiunto
              autoComplete="off"
              onChange={(e) =>
                setFilter({ ...filter, lastName: e.target.value })
              }
            />
          </div>
          <div className="form-group col-md-4">
            <Input
              id="fiscal_code"
              required
              label="Codice Fiscale"
              type="text"
              name="fiscal_code"
              value={filter.fiscalCode} // Aggiunto
              autoComplete="off"
              onChange={(e) =>
                setFilter({ ...filter, fiscalCode: e.target.value })
              }
            />
          </div>

          <div className="form-group col-md-4">
            <Input
              id="email"
              required
              label="Email"
              type="text"
              name="email"
              value={filter.email} // Aggiunto
              autoComplete="off"
              onChange={(e) => setFilter({ ...filter, email: e.target.value })}
            />
          </div>
          <div className="form-group col-md-3">
            <Input
              id="phone_number"
              required
              label="Telefono"
              type="text"
              name="phone_number"
              value={filter.phoneNumber} // Aggiunto
              autoComplete="off"
              onChange={(e) =>
                setFilter({ ...filter, phoneNumber: e.target.value })
              }
            />
          </div>

          <div className="form-group col-md-2">
            <label htmlFor="userState" className="active">
              Stato
            </label>
            <select
              id="userState"
              className="form-control"
              value={filter.state}
              onChange={(e) =>
                setFilter({ ...filter, state: e.target.value })
              }
            >
              <option value="">Tutti gli stati</option>
              <option value="LOADED">Da validare</option>
              <option value="VALIDATED">Validato</option>
              <option value="REFUSED">Rifiutato</option>
              <option value="UPLOADED">Caricato</option>
              <option value="SUSPEND">Sospeso</option>
            </select>
          </div>

          <div className="form-group col-md-3 text-right">
            <button
              type="button"
              className="btn btn-outline-primary m-2"
              onClick={clearFilter}
            >
              Annulla
            </button>

            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={sendFilter}
            >
              Cerca
            </button>
          </div>
        </div>

        {/*Fine cerca*/}
        <div className="row float-end">
          <strong>
            Utenti trovati:{" "}
            <span className="badge rounded-pill bg-primary text-white">
              {usersListTemp?.length || 0}
            </span>{" "}
          </strong>
        </div>

      {sortedUsers && sortedUsers.length > 0 && (
          <table className="table mt-3">
            <thead>
              <tr>
                <th
                  scope="col"
                  onClick={() => requestSort("fiscalCode")}
                  style={{ cursor: "pointer" }}
                >
                  Codice Fiscale
                  {renderSortIcon("fiscalCode")}
                </th>

                <th
                  scope="col"
                  onClick={() => requestSort("lastName")}
                  style={{ cursor: "pointer" }}
                >
                  Cognome Nome
                  {renderSortIcon("lastName")}
                </th>

                <th
                  scope="col"
                  className="text-center"
                  onClick={() => requestSort("isee")}
                  style={{ cursor: "pointer" }}
                >
                  Ultimo ISEE
                  {renderSortIcon("isee")}
                </th>

                <th
                  scope="col"
                  onClick={() => requestSort("birthDate")}
                  style={{ cursor: "pointer" }}
                >
                  Data nascita
                  {renderSortIcon("birthDate")}
                </th>

                <th
                  scope="col"
                  onClick={() => requestSort("dataAggAnpr")}
                  style={{ cursor: "pointer" }}
                >
                  Agg ANPR
                  {renderSortIcon("dataAggAnpr")}
                </th>

                <th scope="col"></th>
              </tr>
            </thead>

            <tbody>
              {usersList.map((x, index) => {
                const lastIsee = getLastIsee(x);
                return (
                <React.Fragment key={x.id || index}>
                  <tr
                    className={`${
                      x.state === "LOADED"
                        ? "table-success"
                        : x.state === "SUSPEND"
                          ? "table-secondary"
                          : ""
                    }`}
                  >
                    <th scope="row">{x.fiscalCode}</th>
                    <td>
                      {" "}
                      {x.lastName} {x.firstName}
                    </td>

                    <td className="text-right">
                      {lastIsee ? (
                        <>
                          {lastIsee.value !== null && (
                            <>
                              <span>
                                {parseFloat(
                                  lastIsee.value,
                                ).toLocaleString("it-IT", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                € (<b>{lastIsee.year}</b>)
                              </span>
                              <br />
                              <EmailIcon email={x.email} />
                              <PhoneIcon phone={x.phoneNumber} />
                              {lastIsee.state ===
                              "VALIDATED" ? (
                                <b className="text-success">
                                  <span
                                    className="icon-wrapper"
                                    title="Validato"
                                  >
                                    <svg className="icon icon-success">
                                      <use
                                        href={`${iconSprite}#it-check-circle`}
                                      ></use>
                                    </svg>
                                  </span>
                                </b>
                              ) : lastIsee.state ===
                                "LOADED" ? (
                                <b className="text-success">
                                  <span
                                    className="icon-wrapper"
                                    title="Caricato"
                                  >
                                    <svg className="icon icon-warning">
                                      <use
                                        href={`${iconSprite}#it-warning`}
                                      ></use>
                                    </svg>
                                  </span>
                                </b>
                              ) : lastIsee.state ===
                                "REFUSED" ? (
                                <b className="text-success">
                                  <span
                                    className="icon-wrapper"
                                    title="Rifiutato"
                                  >
                                    <svg className="icon icon-danger">
                                      <use
                                        href={`${iconSprite}#it-error`}
                                      ></use>
                                    </svg>
                                  </span>
                                </b>
                              ) : (
                                lastIsee.state
                              )}
                              {lastIsee.hasFile === true ? (
                                <b className="text-success">
                                  <span title="Allegato caricato">
                                    <svg className="icon icon-secondary">
                                      <use href={`${iconSprite}#it-file`}></use>
                                    </svg>
                                  </span>
                                </b>
                              ) : (
                                ""
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <>N/A</>
                      )}
                    </td>
                    <td>
                      {x.birthDate}

                      
                    </td>

                    <td>{(() => {
                        const status = getAnprStatus(
                          x.dataAggAnpr,
                          x.dataDecesso,
                        );

                        return (
                          <span
                            className="d-inline-flex align-middle ms-2"
                            title={
                              x.dataDecesso
                                ? `Deceduto il ${x.dataDecesso}`
                                : x.dataAggAnpr
                                  ? `Ultimo aggiornamento ANPR: ${x.dataAggAnpr}`
                                  : "Mai aggiornato ANPR"
                            }
                          >
                            <svg className={`icon icon-${status}`}>
                              <use href={`${iconSprite}#it-user`} />
                            </svg>
                          </span>
                        );
                      })()}</td>
                    <td className="text-right">
                      <Link
                        to={`/lista_utenti/dati_utente?citizen=${x.id}`}
                        className="text-decoration-none"
                      >
                        <div
                          className="btn btn-outline-primary p-1 d-inline-flex align-items-center"
                          title="Dettaglio"
                        >
                          <svg className="icon icon-primary">
                            <use href={`${iconSprite}#it-search`} />
                          </svg>
                        </div>
                      </Link>

                      {lastIsee ? (
                        <button
                          className="btn btn-outline-secondary disabled p-1 ml-1"
                          type="button"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Non è possibile cancellare un utente se ha dati collegati!"
                        >
                          <svg className="icon icon-secondary">
                            <use href={`${iconSprite}#it-delete`} />
                          </svg>
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-danger p-1 ml-1"
                          type="button"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="È possibile eliminare l'utente solo se non ha caricato ISEE"
                          onClick={() => {
                            toggleModal(!isOpen);
                            setRemovableUser({
                              firstName: x.firstName,
                              lastName: x.lastName,
                              id: x.id,
                            });
                          }}
                        >
                          <svg className="icon icon-danger">
                            <use href={`${iconSprite}#it-delete`} />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
              })}
            </tbody>
          </table>
        )}
        {usersListTemp && usersListTemp.length > 0 ? (
          <>
            <Pagination
              key={`${toSearch(filter)}|${sortedUsers.length}|${itemsPerPage}`}
              items={sortedUsers}
              setCurrentItems={setUsersList}
              itemsPerPage={itemsPerPage}
            />

            <div className="row mt-4">
              <div className="col-lg-11">
                <p className="mt-2">
                  {itemsPerPage < sortedUsers.length ? (
                    <>
                      Mostro <strong>{itemsPerPage}</strong> su{" "}
                      <strong>{sortedUsers.length}</strong>
                    </>
                  ) : (
                    <>
                      Mostro <strong>{usersListTemp.length}</strong> su{" "}
                      <strong>{sortedUsers.length}</strong>
                    </>
                  )}
                </p>
              </div>

              <div className="col-lg-1">
                <Select
                  options={[
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                    { value: 50, label: "50" },
                    { value: 100, label: "100" },
                  ]}
                  onChange={(e) => setItemsPerPage(e.value)}
                  placeholder={itemsPerPage.toString()}
                />
              </div>
            </div>
          </>
        ) : (
          <span className="text mt-3">Non ci sono utenti.</span>
        )}
      </div>

      <ModalComponent
        toggleModal={closeRemoveUserModal}
        isOpen={isOpen}
        header={
          removableUserName
            ? "Rimozione utente: " + removableUserName
            : "Rimozione utente"
        }
        body={
          removableUserName
            ? "Confermi di voler rimuovere " + removableUserName + "?"
            : "Confermi di voler rimuovere l'utente selezionato?"
        }
        buttonConfirm="Conferma"
        buttonDeny="Annulla"
        function={() => removeUserAPI()}
      ></ModalComponent>

      {loading && <Loader></Loader>}
    </>
  );
}

export default ListUsers;
